// @flow
import type {CartDataForGlobalDataLayerType} from 'constants/flow/flowTypesGlobalDataLayer';
import type {QuoteItemType, QuotePreparedItemType, TotalsType} from 'constants/flow/flowTypesCart';

import {emptyStringIsFalsy, getAttribute} from '../../helpers';

/**
 * Get the badge for the product on the Cart page.
 *
 * @param {Array<{code: string, title: string}> | string} badgesList - Badges list.
 * @returns {string} Badge value.
 */
export const getBadge = (badgesList: Array<{code: string, title: string}> | string): string =>
    emptyStringIsFalsy(
        Array.isArray(badgesList) ? badgesList.map(item => item.title).join(' / ') : badgesList,
    );

/**
 * Prepare quote item
 * @param {QuoteItemType} item - Quote item
 * @returns {QuotePreparedItemType} Prepared quote item
 */
export const prepareProductData = (item: QuoteItemType): QuotePreparedItemType => ({
    attribute_set: getAttribute(item.type),
    brand: emptyStringIsFalsy(item.brand),
    color: item.frame ? emptyStringIsFalsy(item.frame.color) : '',
    frame_sku: emptyStringIsFalsy(item.product_sku),
    frame_url_path: emptyStringIsFalsy(item.frame_url_path),
    full_sku: emptyStringIsFalsy(item.sku),
    gender: emptyStringIsFalsy(item.gender),
    glasses_type: emptyStringIsFalsy(item.glasses_type),
    item_price: (item.frame && item.frame.price) || item.specialPrice || item.price || '',
    lens_type: emptyStringIsFalsy(item.lens_type),
    previous_category_id: emptyStringIsFalsy(item.previous_category_id),
    previous_category_name: emptyStringIsFalsy(item.previous_category_name),
    product_id: emptyStringIsFalsy(item.product_id),
    product_name: emptyStringIsFalsy(item.name),
    product_badge: getBadge(item.badges),
    quantity: Number(item.qty),
    size: item.frame ? emptyStringIsFalsy(item.frame.frameSize) : '',
    window_name: emptyStringIsFalsy(item.window_name),
    product_img: emptyStringIsFalsy(item.img),
});

/**
 * Get fields for Global Data Layer for cart page.
 *
 * @namespace
 * @property {TotalsType} totals - totals cart
 * @property {Array<QuoteItemType>} products - quote items
 * @returns {CartDataForGlobalDataLayerType} - cart page global data layer
 */
export function getCartDataGlobalDataLayer({
    totals,
    products,
}: {
    totals: TotalsType,
    products: Array<QuoteItemType>,
}): CartDataForGlobalDataLayerType {
    const {discount = '', total = 0, subtotal = 0} = totals;

    return {
        page_type_param1: 'CART',
        page_type_param2: 'CART',
        discount_amount: discount,
        grandtotal: total,
        subtotal,
        products: products.map(prepareProductData),
    };
}
